import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../../../shared/models/option.model';
import { ProvinceService } from '../../../../services/data/province.service';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Model193Service } from '../../model-193.service';
import {
  Model193Recipient,
  Model193Validation,
  normalizarPerceptor193,
  nuevoPerceptor193,
  ORIGEN_CONTABILIDAD,
  ORIGEN_MANUAL
} from '../taxes-193.model';

export interface Model193RecipientDialogData {
  row: Model193Recipient | null;
  ejercicio: number;
  validaciones: Model193Validation[];
  naturalezaDeclarante: boolean;
}

const IMPORTES: string[] = [
  'importePercepciones', 'importeReducciones', 'base', 'porcentaje', 'retencion', 'penalizaciones',
  'compensaciones', 'garantias', 'haciendaEstatal', 'navarra', 'alava', 'gipuzkoa', 'bizkaia'
];

const BOOLEANOS: string[] = ['mediador', 'pendiente'];

const NATURALEZAS: { [clave: string]: string[] } = {
  A: [
    '01 - Primas por asistencia a juntas',
    '02 - Dividendos y participaciones en beneficios de cualquier entidad',
    '03 - Rendimientos de activos que facultan para participar en beneficios, ventas, operaciones o ingresos',
    '04 - Constitución o cesión de derechos de uso o disfrute sobre valores o participaciones',
    '05 - Otra utilidad procedente de una entidad por la condición de socio o partícipe',
    '06 - Rendimientos exentos',
    '07 - Dividendos y beneficios distribuidos por instituciones de inversión colectiva',
    '08 - Dividendos no sometidos a retención ni ingreso a cuenta'
  ],
  B: [
    '01 - Intereses de obligaciones, bonos, certificados de depósito u otros títulos privados',
    '02 - Intereses de obligaciones, bonos, cédulas, deuda pública u otros títulos públicos',
    '03 - Intereses de préstamos no bancarios',
    '04 - Régimen transitorio de beneficios en operaciones financieras',
    '05 - Rentas satisfechas por una entidad financiera por la cesión de un crédito',
    '06 - Otros rendimientos de capital mobiliario o rentas',
    '07 - Rendimientos exentos'
  ],
  C: [
    '01 - Propiedad intelectual cuando el perceptor no es el autor',
    '02 - Propiedad industrial no afecta a actividades económicas',
    '03 - Prestación de asistencia técnica',
    '04 - Arrendamiento o subarrendamiento de bienes muebles, negocios o minas',
    '05 - Rentas vitalicias o temporales por imposición de capitales',
    '06 - Cesión del derecho a la explotación de la imagen',
    '07 - Subarrendamiento de bienes inmuebles urbanos',
    '08 - Cesión de la explotación de la imagen en explotaciones económicas',
    '09 - Premios de juegos, concursos, rifas o combinaciones aleatorias',
    '10 - Cargos de administrador o consejero en otras sociedades',
    '11 - Rendimientos exentos',
    '12 - Otros rendimientos o rentas a integrar en la base imponible general',
    '13 - Otros rendimientos o rentas a integrar en la base imponible del ahorro',
    '14 - Otros rendimientos o rentas de perceptores que no son contribuyentes del IRPF',
    '15 - Anticipos a cuenta por la cesión de derechos de autor'
  ]
};

NATURALEZAS['D'] = NATURALEZAS['B'];

function numero(valor: any): number {
  const convertido = Number(String(valor ?? '').replace(',', '.'));

  return isNaN(convertido) ? 0 : convertido;
}

function dosDigitos(valor: number): string {
  return valor < 10 ? '0' + valor : String(valor);
}

@Component({
  selector: 'app-model-193-recipient-dialog',
  templateUrl: './model-193-recipient-dialog.component.html',
  styleUrls: ['./model-193-recipient-dialog.component.css'],
  standalone: false
})
export class Model193RecipientDialogComponent implements OnInit {

  f: any = null;
  saving = false;
  validaciones: Model193Validation[] = [];

  provinciaOptions: Option[] = [];

  fechaDevengo = new UntypedFormControl(null);
  fechaInicioPrestamo = new UntypedFormControl(null);
  fechaVencimientoPrestamo = new UntypedFormControl(null);

  readonly sinoOptions: Option[] = [{ label: 'No', value: 'N' }, { label: 'Sí', value: 'S' }];

  readonly claveOptions: Option[] = [
    { label: 'A - Participación en fondos propios de entidades', value: 'A' },
    { label: 'B - Cesión a terceros de capitales propios', value: 'B' },
    { label: 'C - Otros rendimientos del capital mobiliario o rentas', value: 'C' },
    { label: 'D - Cesión de capitales propios a entidades vinculadas', value: 'D' }
  ];

  readonly tipoPercepcionOptions: Option[] = [
    { label: 'Dineraria', value: '1' },
    { label: 'En especie', value: '2' }
  ];

  readonly pagoOptions: Option[] = [
    { label: '1 - Como emisor', value: '1' },
    { label: '2 - Como mediador de valor nacional', value: '2' },
    { label: '3 - Como mediador de valor extranjero', value: '3' },
    { label: '4 - Como mediador de valor extranjero no retenedor', value: '4' },
    { label: '5 - Como mediador de otro tipo de rendimientos', value: '5' }
  ];

  readonly claveCodigoOptions: Option[] = [
    { label: '1 - El código emisor es un NIF', value: '1' },
    { label: '2 - El código emisor es un código ISIN', value: '2' },
    { label: '3 - Valores extranjeros sin ISIN', value: '3' },
    { label: '4 - Identificación por ISIN y NIF del emisor', value: '4' }
  ];

  readonly tipoCodigoOptions: Option[] = [
    { label: 'C - Código cuenta cliente', value: 'C' },
    { label: 'O - Otra identificación', value: 'O' },
    { label: 'P - Préstamo de valores', value: 'P' }
  ];

  readonly claveMercadoOptions: Option[] = [
    { label: 'A - Mercado secundario oficial español', value: 'A' },
    { label: 'B - Mercado oficial de valores extranjeros de la UE', value: 'B' },
    { label: 'C - Otros mercados oficiales extranjeros', value: 'C' },
    { label: 'D - Otros', value: 'D' }
  ];

  readonly ceutaOptions: Option[] = [
    { label: '0 - No', value: '0' },
    { label: '1 - Ceuta o Melilla', value: '1' },
    { label: '2 - Isla de La Palma', value: '2' }
  ];

  readonly rutasForales: { ruta: string, etiqueta: string }[] = [
    { ruta: 'haciendaEstatal', etiqueta: 'Hacienda Estatal' },
    { ruta: 'navarra', etiqueta: 'Comunidad Foral de Navarra' },
    { ruta: 'alava', etiqueta: 'Diputación Foral de Araba/Álava' },
    { ruta: 'gipuzkoa', etiqueta: 'Diputación Foral de Gipuzkoa' },
    { ruta: 'bizkaia', etiqueta: 'Diputación Foral de Bizkaia' }
  ];

  static open(dialog: MatDialog, data: Model193RecipientDialogData): Observable<boolean> {
    return dialog.open<Model193RecipientDialogComponent, Model193RecipientDialogData, boolean>(
      Model193RecipientDialogComponent, {
      width: '1080px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data
    }).afterClosed().pipe(map(resultado => resultado === true));
  }

  constructor(
    private dialogRef: MatDialogRef<Model193RecipientDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: Model193RecipientDialogData,
    private service: Model193Service,
    private provinceService: ProvinceService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    this.validaciones = this.data.validaciones || [];

    const perceptor = this.data.row ? normalizarPerceptor193(this.data.row) : nuevoPerceptor193();

    this.f = this.aFormulario(perceptor);
    this.actualizarNaturalezas();
    this.fechaDevengo.setValue(this.aFecha(perceptor.fechaDevengo, 'ddMMyyyy'));
    this.fechaInicioPrestamo.setValue(this.aFecha(perceptor.fechaInicioPrestamo, 'yyyyMMdd'));
    this.fechaVencimientoPrestamo.setValue(this.aFecha(perceptor.fechaVencimientoPrestamo, 'yyyyMMdd'));

    this.provinceService.getList().subscribe({
      next: respuesta => {
        this.provinciaOptions = (respuesta.data || [])
          .map((item: any) => ({ label: item.provinceName, value: item.provinceCode }));
      },
      error: () => { }
    });
  }

  get titulo(): string {
    return this.data.row ? 'Editar perceptor' : 'Nuevo perceptor';
  }

  get subtitulo(): string {
    if (this.data.row && this.data.row.origen === ORIGEN_CONTABILIDAD) {
      return `Ejercicio ${this.data.ejercicio} · Calculado de la contabilidad: al guardar se sustituye por tu versión`;
    }

    return `Ejercicio ${this.data.ejercicio}`;
  }

  get esValores(): boolean {
    return ['A', 'B', 'D'].indexOf(this.f?.clavePercepcion) !== -1;
  }

  get esPrestamo(): boolean {
    return this.f?.tipoCodigo === 'P';
  }

  naturalezaOptions: Option[] = [];

  private actualizarNaturalezas() {
    this.naturalezaOptions = (NATURALEZAS[this.f?.clavePercepcion] || []).map(texto => ({ label: texto, value: texto.substring(0, 2) }));
  }


  mensajes(): Model193Validation[] {
    return this.validaciones;
  }

  trackByIndex(i: number) { return i; }

  private aFormulario(perceptor: Model193Recipient): any {
    const copia: any = { ...perceptor };

    IMPORTES.forEach(campo => copia[campo] = copia[campo] ? String(copia[campo]) : '');
    copia.ejercicioDevengo = copia.ejercicioDevengo ? String(copia.ejercicioDevengo) : '';
    BOOLEANOS.forEach(campo => copia[campo] = copia[campo] ? 'S' : 'N');

    return copia;
  }

  private aFecha(texto: string, formato: 'ddMMyyyy' | 'yyyyMMdd'): Date | null {
    const digitos = (texto || '').replace(/\D/g, '');

    if (digitos.length !== 8) return null;

    const dia = formato === 'ddMMyyyy' ? digitos.substring(0, 2) : digitos.substring(6, 8);
    const mes = digitos.substring(formato === 'ddMMyyyy' ? 2 : 4, formato === 'ddMMyyyy' ? 4 : 6);
    const anio = formato === 'ddMMyyyy' ? digitos.substring(4, 8) : digitos.substring(0, 4);
    const fecha = new Date(Number(anio), Number(mes) - 1, Number(dia));

    return isNaN(fecha.getTime()) ? null : fecha;
  }

  private aTexto(valor: any, formato: 'ddMMyyyy' | 'yyyyMMdd'): string {
    if (!valor) return '';

    const fecha = valor instanceof Date ? valor : new Date(valor);

    if (isNaN(fecha.getTime())) return '';

    const dia = dosDigitos(fecha.getDate());
    const mes = dosDigitos(fecha.getMonth() + 1);
    const anio = String(fecha.getFullYear());

    return formato === 'ddMMyyyy' ? dia + mes + anio : anio + mes + dia;
  }

  private aDto(): Model193Recipient {
    const copia: any = { ...this.f };

    IMPORTES.forEach(campo => copia[campo] = numero(copia[campo]));
    copia.ejercicioDevengo = Math.trunc(numero(copia.ejercicioDevengo));
    BOOLEANOS.forEach(campo => copia[campo] = copia[campo] === 'S');
    copia.fechaDevengo = this.aTexto(this.fechaDevengo.value, 'ddMMyyyy');
    copia.fechaInicioPrestamo = this.aTexto(this.fechaInicioPrestamo.value, 'yyyyMMdd');
    copia.fechaVencimientoPrestamo = this.aTexto(this.fechaVencimientoPrestamo.value, 'yyyyMMdd');

    if (!this.data.row) {
      copia.uid = null;
      copia.clave = null;
      copia.origen = ORIGEN_MANUAL;
      copia.sobrescribeClave = null;
    } else if (this.data.row.origen === ORIGEN_CONTABILIDAD) {
      copia.uid = null;
      copia.clave = null;
      copia.origen = ORIGEN_MANUAL;
      copia.sobrescribeClave = this.data.row.clave;
    }

    return copia as Model193Recipient;
  }

  get(ruta: string): any {
    return this.f[ruta];
  }

  set(ruta: string, valor: any) {
    this.f[ruta] = valor;

    if (ruta === 'clavePercepcion') {
      this.f.naturaleza = '';
      this.actualizarNaturalezas();
    }
  }

  guardar() {
    if (this.saving) return;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const dto = this.aDto();
    const fila = this.data.row;
    const tarea = fila && fila.origen === ORIGEN_MANUAL && fila.uid
      ? this.service.updateRecipient(fila.uid, dto)
      : this.service.createRecipient(this.data.ejercicio, dto);

    tarea.then(() => {
      this.saving = false;
      this.dialogRef.disableClose = false;
      this.notification.ok('Perceptor guardado');
      this.dialogRef.close(true);
    }).catch(err => {
      this.saving = false;
      this.dialogRef.disableClose = false;
      this.notification.error(err?.error?.error?.messages?.[0] || 'No se ha podido guardar el perceptor');
    });
  }

  cerrar() {
    if (this.saving) return;

    this.dialogRef.close(false);
  }
}
